import { MrfiktivHttpClientProvider } from "@/services/mrfiktiv/mrfiktiv-http-client.provider";
import {
  MrfiktivPartnerVideoControllerGetOneParamsGen,
  MrfiktivPartnerVideoControllerGetOneWithAccessSignatureParamsGen,
  MrfiktivPartnerVideoControllerGetParamsGen,
  MrfiktivSetVideoChaptersDtoGen,
  MrfiktivTranscriptViewModelGen,
  MrfiktivUpdatePartnerVideoDtoGen,
  MrfiktivVideoViewModelGen
} from "../v1/data-contracts";
import { Video } from "../v1/Video";

/**
 * Communicate with Video API
 */
class VideoService {
  private proxy: Video;

  /**
   * Construct service
   */
  constructor(mrfiktivHttpClientProvider: MrfiktivHttpClientProvider) {
    this.proxy = new Video(mrfiktivHttpClientProvider.client());
  }

  /**
   * Create a video
   */
  async create(partnerId: string, dto: object) {
    return (await this.proxy.partnerVideoControllerCreate(partnerId, dto)).data;
  }

  /**
   * Update metadata of a video after upload
   */
  async updateMetadata(
    partnerId: string,
    videoId: string,
    dto: MrfiktivUpdatePartnerVideoDtoGen
  ): Promise<MrfiktivVideoViewModelGen> {
    return (await this.proxy.partnerVideoControllerUpdateMetadata(partnerId, videoId, dto)).data;
  }

  /**
   * Get all videos for a partner (filtered server-side if needed)
   */
  async getAll(query: MrfiktivPartnerVideoControllerGetParamsGen) {
    return (await this.proxy.partnerVideoControllerGet(query)).data;
  }

  /**
   * Get one video by id
   */
  async getOne(
    partnerId: string,
    id: string,
    modifiers?: Partial<Omit<MrfiktivPartnerVideoControllerGetOneParamsGen, "partnerId" | "videoId">>
  ) {
    return (await this.proxy.partnerVideoControllerGetOne({ ...modifiers, partnerId, videoId: id })).data;
  }
  /**
   * Get one video by id
   */
  async getOneShared(access: MrfiktivPartnerVideoControllerGetOneWithAccessSignatureParamsGen) {
    return (await this.proxy.partnerVideoControllerGetOneWithAccessSignature(access)).data;
  }

  /**
   * Delete a video by id
   */
  async delete(partnerId: string, id: string) {
    return (await this.proxy.partnerVideoControllerDeleteOne(partnerId, id)).data;
  }

  /**
   * Cancel a video upload by id
   */
  async cancel(partnerId: string, id: string) {
    return (await this.proxy.partnerVideoControllerCancel(partnerId, id)).data;
  }

  /**
   * Get the auto-generated transcript
   */
  async getTranscript(partnerId: string, id: string): Promise<MrfiktivTranscriptViewModelGen> {
    return (await this.proxy.partnerVideoControllerGetTranscript(partnerId, id)).data;
  }

  /**
   * Trigger AI chapter generation for a video
   */
  async generateChapters(partnerId: string, videoId: string): Promise<MrfiktivVideoViewModelGen> {
    return (await this.proxy.partnerVideoControllerGenerateChapters(partnerId, videoId)).data;
  }

  /**
   * Set chapters for a video
   */
  async setChapters(
    partnerId: string,
    videoId: string,
    dto: MrfiktivSetVideoChaptersDtoGen
  ): Promise<MrfiktivVideoViewModelGen> {
    return (await this.proxy.partnerVideoControllerSetChapters(partnerId, videoId, dto)).data;
  }

  /**
   * Delete all chapters for a video
   */
  async deleteChapters(partnerId: string, videoId: string): Promise<MrfiktivVideoViewModelGen> {
    return (await this.proxy.partnerVideoControllerDeleteChapters(partnerId, videoId)).data;
  }
}

/**
 * Communicate with Video API
 */
export default new VideoService(new MrfiktivHttpClientProvider());
